import { useState, useRef } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import { ZoomIn, ZoomOut, ChevronLeft, ChevronRight } from 'lucide-react'
import 'react-pdf/dist/esm/Page/AnnotationLayer.css'
import 'react-pdf/dist/esm/Page/TextLayer.css'

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`

interface Highlight {
  id: string
  page_number: number
  bbox: {
    x0: number
    y0: number
    x1: number
    y1: number
  }
  confidence: number
  metadata: Record<string, any>
}

interface PDFViewerWithHighlightsProps {
  pdfUrl: string
  highlights?: Highlight[]
  onHighlightClick?: (highlight: Highlight) => void
  onPageChange?: (page: number) => void
}

export default function PDFViewerWithHighlights({
  pdfUrl,
  highlights = [],
  onHighlightClick,
  onPageChange,
}: PDFViewerWithHighlightsProps) {
  const [numPages, setNumPages] = useState<number | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [scale, setScale] = useState(1.2)
  const containerRef = useRef<HTMLDivElement>(null)

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages)
    setCurrentPage(1)
    onPageChange?.(1)
  }

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1)
      onPageChange?.(currentPage - 1)
    }
  }

  const goToNextPage = () => {
    if (currentPage < (numPages || 1)) {
      setCurrentPage(currentPage + 1)
      onPageChange?.(currentPage + 1)
    }
  }

  const zoomIn = () => {
    setScale((prev) => Math.min(prev + 0.2, 3))
  }

  const zoomOut = () => {
    setScale((prev) => Math.max(prev - 0.2, 0.6))
  }

  // Get highlights for current page
  const pageHighlights = highlights.filter((h) => h.page_number === currentPage)

  return (
    <div className="flex flex-col h-full bg-gray-100">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 bg-white border-b border-gray-200">
        <div className="flex items-center gap-2">
          <button
            onClick={goToPreviousPage}
            disabled={currentPage <= 1}
            className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span className="text-sm font-medium text-gray-700">
            Page {currentPage} of {numPages || '?'}
          </span>
          <button
            onClick={goToNextPage}
            disabled={currentPage >= (numPages || 1)}
            className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={zoomOut}
            className="p-2 rounded-lg hover:bg-gray-100"
          >
            <ZoomOut className="w-5 h-5" />
          </button>
          <span className="text-sm text-gray-600 w-12 text-center">
            {Math.round(scale * 100)}%
          </span>
          <button
            onClick={zoomIn}
            className="p-2 rounded-lg hover:bg-gray-100"
          >
            <ZoomIn className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* PDF Container */}
      <div 
        ref={containerRef}
        className="flex-1 overflow-auto p-4 flex justify-center"
      >
        <Document
          file={pdfUrl}
          onLoadSuccess={onDocumentLoadSuccess}
          loading={
            <div className="flex items-center justify-center h-full text-gray-500">
              Loading PDF...
            </div>
          }
          error={
            <div className="flex items-center justify-center h-full text-red-500">
              Failed to load PDF.
            </div>
          }
          className="flex flex-col items-center gap-4"
        >
          <Page
            pageNumber={currentPage}
            scale={scale}
            className="relative shadow-lg"
            renderAnnotationLayer={false}
            renderTextLayer={false}
          />
        </Document>
      </div>

      {/* Highlights overlay info */}
      {pageHighlights.length > 0 && (
        <div className="px-4 py-2 bg-purple-50 border-t border-purple-200 text-xs text-purple-700">
          <div className="font-medium">
            {pageHighlights.length} table{pageHighlights.length > 1 ? 's' : ''} detected on this page.
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {pageHighlights.map((highlight, index) => (
              <button
                key={highlight.id || `${highlight.page_number}-${index}`}
                type="button"
                onClick={() => onHighlightClick?.(highlight)}
                className="rounded-md border border-purple-200 bg-white px-2 py-1 text-purple-700 hover:bg-purple-100"
              >
                Table {highlight.metadata?.table_index ?? index + 1}
                {typeof highlight.confidence === 'number' ? ` · ${Math.round(highlight.confidence * 100)}%` : ''}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
